import i18nFactory from '@yandex-int/i18n';
import { Link, Spin } from 'lego-on-react';
import { flatten } from 'lodash';
import * as React from 'react';

import { alert } from '../../service/Alert/Alert';
import { Bem } from '../../service/Bem/Bem';
import { pluralizeWord } from '../../service/Pluralize/Pluralize';
import { UrlHelpers } from '../../service/UrlHelpers';
import { BannerImageSize } from '../../typings/domain';
import { ModalDecorator } from '../ModalDecorator/ModalDecorator';
import { Shape2, Shape2Icon } from '../Shape2/Shape2';

import './ImageCanvasIFrame.css';
import * as keyset from './ImageCanvasIFrame.i18n';

const i18n = i18nFactory(keyset);

export interface OwnProps {
    isMultipleUpload: boolean;
    isVisible?: boolean;

    canvasDomain: string;
    pageType?: CanvasPageType;
    clientId?: string;
    fixedSize?: BannerImageSize;
    limit?: number;
    isMassAction?: boolean;

    urlBuilder?: (
        canvasDomain: string,
        pageType: CanvasPageType,
        params: string
    ) => string;
    onImageCanvasSelectIds: (ids: Array<string>) => void;
    onClose: () => void;
}

export interface StateProps {
    sharedData?: CanvasSharedData;
}

export type ImageCanvasIFrameProps = OwnProps & StateProps;

export type CanvasSharedData = Record<string, number | string>;

export interface ImageCanvasIFrameState {
    isLoading: boolean;
}

export enum CanvasPageType {
    presets = 'presets',
    creatives = 'creatives',
    adaptive = 'presets/5/creatives/add',
    html5Add = 'html5/add',
    html5 = 'html5',
    ideas = 'ideas'
}

export enum ImageCanvasIFrameMessage {
    ready = 'ready',
    creativesSelect = 'creatives-select',
    close = 'close'
}

const BLOCK = 'image-canvas-iframe';

export class ImageCanvasIFrame extends React.Component<
    ImageCanvasIFrameProps,
    ImageCanvasIFrameState
> {
    private iframeRef: HTMLIFrameElement;

    constructor(props: ImageCanvasIFrameProps) {
        super(props);

        this.state = { isLoading: true };
    }

    componentWillMount() {
        window.addEventListener('message', this.onMessage);
    }

    componentWillUnmount() {
        window.removeEventListener('message', this.onMessage);
    }

    render() {
        const { isVisible, onClose } = this.props;
        const { isLoading } = this.state;

        return (
            <ModalDecorator
                visible={Boolean(isVisible)}
                cls={Bem.cls(BLOCK)}
                theme="dark-paranja"
                onOutsideClick={onClose}
            >
                <div
                    className={Bem.cls({
                        block: BLOCK,
                        mods: { loaded: !isLoading }
                    })}
                >
                    <div className={Bem.cls(BLOCK, 'paranja')}>
                        <Spin
                            cls={Bem.cls(BLOCK, 'spin')}
                            size="l"
                            progress={isLoading}
                        />
                    </div>
                    <Link
                        cls={Bem.cls(BLOCK, 'close')}
                        theme="pseudo"
                        onClick={onClose}
                    >
                        <Shape2
                            icon={Shape2Icon.close}
                            width={15}
                            height={15}
                        />
                    </Link>
                    <iframe
                        className={Bem.cls(BLOCK, 'iframe')}
                        frameBorder="0"
                        ref={this.setIframeHref}
                        src={this.createImageCanvasUrl()}
                        onLoad={this.onFrameLoaded}
                    />
                </div>
            </ModalDecorator>
        );
    }

    private createImageCanvasUrl = () => {
        const {
            canvasDomain,
            clientId,
            fixedSize,
            urlBuilder,
            sharedData
        } = this.props;

        const pageType = this.props.pageType || CanvasPageType.creatives;
        const isGeoproduct = Boolean(sharedData && sharedData.cpm_geoproduct);

        let productType;
        let creativeType;

        switch (true) {
            case isGeoproduct:
                productType = 'CPM_GEOPRODUCT';
                creativeType = 'cpmGeoproduct';
                break;

            case pageType === CanvasPageType.adaptive:
                creativeType = 'adaptive';
                break;

            case [CanvasPageType.html5, CanvasPageType.html5Add].includes(
                pageType
            ):
                productType = 'GENERAL';
                break;

            default:
                break;
        }

        const params = encodeURIComponent(
            JSON.stringify({
                isCompact: true,
                clientId: clientId,
                creativeType,
                filters:
                    productType !== 'GENERAL' && fixedSize
                        ? {
                              sizes: [
                                  fixedSize.isAdaptive
                                      ? 'adaptive'
                                      : {
                                            w: fixedSize.width,
                                            h: fixedSize.height
                                        }
                              ]
                          }
                        : undefined,
                areFiltersDisabled: Boolean(
                    productType !== 'GENERAL' && fixedSize
                ),
                isAdaptive:
                    pageType === CanvasPageType.adaptive ? true : undefined,
                productType,
                ...sharedData
            })
        );

        if (urlBuilder) {
            return urlBuilder(canvasDomain, pageType, params);
        }

        return UrlHelpers.getCanvasConstructorUrl(
            canvasDomain,
            pageType,
            params
        );
    };

    private setIframeHref = (ref: HTMLIFrameElement) => {
        this.iframeRef = ref;
    };

    private onFrameLoaded = () => {
        this.setState({
            isLoading: false
        });
    };

    private onMessage = (ev: MessageEvent) => {
        const { isMultipleUpload, limit, isMassAction, pageType } = this.props;
        const message = ev.data.type;

        if (!this.iframeRef || this.iframeRef.contentWindow !== ev.source) {
            return;
        }

        switch (message) {
            case ImageCanvasIFrameMessage.creativesSelect:
                const ids = flatten(ev.data.creativeIds) as Array<string>;
                const limitIncludingUpdate = this.updateLimitWithEditingBanner(
                    limit
                );

                if (ids.length > 1 && !isMultipleUpload && !isMassAction) {
                    alert({
                        title: i18n('message-no-multi-title'),
                        children: i18n('message-no-multi-text'),
                        buttonText: i18n('button')
                    }).then(this.unlockCanvas);
                } else if (
                    limitIncludingUpdate &&
                    ids.length > limitIncludingUpdate &&
                    !isMassAction
                ) {
                    this.showLimitWarning(limitIncludingUpdate).then(
                        this.unlockCanvas
                    );
                } else if (
                    pageType &&
                    (pageType === CanvasPageType.html5 ||
                        pageType === CanvasPageType.html5Add)
                ) {
                    this.props.onImageCanvasSelectIds(ev.data
                        .creativeIds[0] as Array<string>);
                } else {
                    this.props.onImageCanvasSelectIds(ev.data
                        .creativeIds as Array<string>);
                }

                break;
            case ImageCanvasIFrameMessage.close:
                // this.resetFocus();
                this.props.onClose();
                break;
            default:
                break;
        }
    };

    private updateLimitWithEditingBanner = (limit: number | undefined) => {
        return limit !== undefined && limit >= 0 ? limit + 1 : undefined;
    };

    private showLimitWarning = (limit: number) => {
        return alert({
            title: pluralizeWord(
                [
                    i18n('one-message-limit-title', {
                        count: limit
                    }),
                    i18n('some-message-limit-title', {
                        count: limit
                    }),
                    i18n('many-message-limit-title', {
                        count: limit
                    })
                ],
                limit
            ),
            children: i18n('message-limit-text'),
            buttonText: i18n('button')
        });
    };

    private unlockCanvas = () => {
        if (this.iframeRef && this.iframeRef.contentWindow) {
            this.iframeRef.contentWindow.postMessage({ type: 'unlock' }, '*');
        }
    };
}
